import { useSyncExternalStore } from "react";
import { api, ApiError } from "./api/client";

/**
 * My favorites, as a set of release keys.
 *
 * One piece of state above every card, the way the player is: a heart on a
 * card in the release view and the same record's row in the track view have
 * to agree, and a filter change that redraws the page must not forget what
 * was marked. Loaded once after sign-in; the whole set is keys and dates.
 *
 * Toggling is optimistic. The set flips at once and the request follows; an
 * ApiError flips it back and is handed to the caller to show.
 */
let keys = new Set<string>();
let loaded = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export async function loadFavorites(): Promise<void> {
  const favs = await api.favorites();
  keys = new Set(favs.map((f) => f.key));
  loaded = true;
  emit();
}

export function forgetFavorites(): void {
  keys = new Set();
  loaded = false;
  emit();
}

export async function toggleFavorite(key: string): Promise<void> {
  const on = !keys.has(key);
  const next = new Set(keys);
  if (on) next.add(key); else next.delete(key);
  keys = next;
  emit();
  try {
    await api.favorite(key, on);
  } catch (e) {
    const back = new Set(keys);
    if (on) back.delete(key); else back.add(key);
    keys = back;
    emit();
    throw e as ApiError;
  }
}

export function useFavorites(): { has: (key: string) => boolean; count: number; loaded: boolean } {
  const snap = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => keys,
    () => keys,
  );
  return { has: (key) => snap.has(key), count: snap.size, loaded };
}
